'use client';

import { browserStore } from '@/constants/browser-store';
import { websiteEnv } from '@/constants/website-env';
import { useAppContext } from '@/context/app';
import { trans } from '@/i18n';
import { generateUrl } from '@/util/imageUtils';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import React, { Dispatch, Fragment, ReactNode, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import HoverPrefetchLink from '../hover-prefetch-link';
import NavigationItem from './navigation-item';

const NavigationAvatar = dynamic(() => import('./navigation-avatar'), {
  ssr: false,
  loading: () => {
    return <div className='h-[75px]' />;
  },
});

type ColorOption = NonNullable<
  NonNullable<NonNullable<NonNullable<GetColorMenusQuery['bridesmaids']>['aggregations']>[0]>['options']
>[0];

interface Props {
  colorMenus: Record<
    'bridesmaids' | 'guest' | 'moms' | 'dresses' | 'prom' | 'homecoming' | 'graduation',
    ColorOption[]
  >;
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  getNavTriggerElement: () => HTMLButtonElement | null;
}

const MOBILE_MENU_DRAWER_CONTENT_CLASS_NAME = 'mobile-menu-drawer-content';

const Navigation = (props: Props) => {
  const { colorMenus, open, onOpenChange, getNavTriggerElement } = props;

  const pathname = usePathname();
  const [{ activeColumn, cartActiveColumn, headerOnTop, headerToolbarHeight, headerHeight }] = useAppContext();
  const isAttire = (pathname === '/cart' ? cartActiveColumn : activeColumn) === 'attire';

  const headerVisibleHeight = headerHeight - (headerOnTop ? 0 : headerToolbarHeight);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  const handleClick: React.MouseEventHandler<HTMLDivElement> = e => {
    const eventTarget = e.target;

    if (eventTarget instanceof Element && eventTarget.closest('a')) {
      onOpenChange(false);
    }
  };

  const menus = useMemo<
    {
      label: ReactNode;
      dropdown: ReactNode;
      href?: string;
      visible: boolean;
    }[]
  >(() => {
    const bridesmaidColorMenu = colorMenus?.bridesmaids?.length
      ? generateColorMenuNode(colorMenus.bridesmaids, trans('SHOP BY COLOR'), '/bridesmaid-dresses')
      : null;
    const guestColorMenu = colorMenus?.guest?.length
      ? generateColorMenuNode(colorMenus.guest, trans('SHOP BY COLOR'), '/wedding-guest-dresses')
      : null;
    const momsColorMenu = colorMenus?.moms.length
      ? generateColorMenuNode(colorMenus.moms, trans('SHOP BY COLOR'), '/mother-of-the-bride-dresses')
      : null;
    const dressesColorMenu = colorMenus?.dresses.length
      ? generateColorMenuNode(colorMenus.dresses, trans('SHOP BY COLOR'), '/dresses')
      : null;
    const promDressesColorMenu = colorMenus?.prom.length
      ? generateColorMenuNode(colorMenus.prom, trans('SHOP BY COLOR'), '/prom-dresses')
      : null;
    const graduationDressesColorMenu = colorMenus?.homecoming.length
      ? generateColorMenuNode(colorMenus.graduation, trans('SHOP BY COLOR'), '/graduation-dresses')
      : null;
    const homecomingDressesColorMenu = colorMenus?.graduation.length
      ? generateColorMenuNode(colorMenus.homecoming, trans('SHOP BY COLOR'), '/homecoming-dresses')
      : null;

    return [
      {
        visible: isAttire,
        label: trans('OCCASION'),
        href: '/dresses',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('OCCASION DRESSES'),
                  children: [
                    {
                      label: trans('All Occasion Dresses'),
                      href: '/dresses',
                    },
                    {
                      label: trans('Holiday Dresses'),
                      href: '/holiday-dresses',
                    },
                    {
                      label: trans('Party Dresses'),
                      href: '/party-dresses',
                    },
                    {
                      label: browserStore.isAustria
                        ? 'Ballkleider'
                        : browserStore.isMexico
                          ? 'Vestidos de Coctel'
                          : trans('Evening Dresses'),
                      href: '/evening-dresses',
                    },
                    {
                      label: trans('Formal Dresses'),
                      href: '/formal-dresses',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY DESIGN'),
                  children: [
                    {
                      label: trans('Sequin Dresses'),
                      href: '/dresses/fabric/sequin',
                    },
                    {
                      label: browserStore.isAustria
                        ? 'Samtgewand'
                        : browserStore.isSwitzerland
                          ? 'Velvetkleid'
                          : trans('Velvet Dresses'),
                      href: '/dresses/fabric/velvet',
                    },
                    {
                      label: trans('Satin Dresses'),
                      href: '/fabric/stretch-satin',
                    },
                    {
                      label: trans('Lace Dresses'),
                      href: '/dresses/fabric/lace',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY COLOR'),
                  children: [
                    {
                      label: trans('Red Dresses'),
                      href: '/dresses/colors-family/red',
                    },
                    {
                      label: trans('Green Dresses'),
                      href: '/dresses/colors-family/green',
                    },
                    {
                      label: trans('Black Dresses'),
                      href: '/dresses/colors-family/black',
                    },
                    {
                      label: trans('Gold Dresses'),
                      href: '/dresses/color/light-gold',
                    },
                    {
                      label: trans('Blue Dresses'),
                      href: '/dresses/colors-family/blue',
                    },
                  ],
                },
              ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                acc.push(
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href }, i) => {
                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>,
                );

                return acc;
              }, [])}
            </ul>

            <ul className=' flex flex-col gap-y-[30px]'>
              {[
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/occasion-new-arrivals.webp`,
                  label: trans('NEW ARRIVALS'),
                  href: '/dresses?sort_by=new',
                },
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/occasion-satin.webp`,
                  label: trans('SATIN'),
                  href: `/dresses/fabric/stretch-satin`,
                },
              ].map(({ src, label, href }, i) => {
                return (
                  <li key={i}>
                    <HoverPrefetchLink
                      href={href}
                      className='peer block'
                    >
                      <Image
                        src={src}
                        width={1022}
                        height={1500}
                        alt={label}
                        className='h-auto w-full max-w-[220px]'
                        unoptimized
                      />
                    </HoverPrefetchLink>
                    <HoverPrefetchLink
                      href={href}
                      className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
                    >
                      {label}
                    </HoverPrefetchLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
      },
      {
        visible: isAttire,
        label: browserStore.isBelgium ? 'BAL' : trans('PROM'),
        href: '/prom-dresses',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('PROM DRESSES'),
                  children: [
                    {
                      label: trans('All Prom Dresses'),
                      href: '/prom-dresses',
                    },
                    {
                      label: websiteEnv.isCanadaFrench ? trans('Under CA$100') : trans('Under €100'),
                      href: '/prom-dresses/price/0-100',
                      visible: !websiteEnv.isSweden,
                    },
                    {
                      label: trans('Long'),
                      href: '/long-prom-dresses',
                    },
                    {
                      label: websiteEnv.isSweden ? trans('Size 48-62') : trans('Plus Size'),
                      href: '/plus-size-prom-dresses',
                      visible: false,
                    },
                    {
                      label: trans('Gold'),
                      href: '/prom-dresses/color/light-gold',
                    },
                    {
                      label: trans('Floral'),
                      href: '/floral-dresses',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY TRENDS'),
                  children: [
                    {
                      label: trans('A-Line'),
                      href: '/prom-dresses/silhouette/a-line',
                    },
                    {
                      label: trans('Ball Gown'),
                      href: '/prom-dresses/silhouette/ball-gown',
                      visible: false,
                    },
                    {
                      label: trans('Mermaid'),
                      href: '/prom-dresses/silhouette/mermaid',
                    },
                    {
                      label: trans('Sequins'),
                      href: '/prom-dresses/embellishment/sequin',
                      visible: false,
                    },
                    {
                      label: trans('Corset'),
                      href: '/prom-dresses/embellishment/corset',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY FABRIC'),
                  children: [
                    {
                      label: trans('Satin'),
                      href: '/prom-dresses/fabric/satin-charmeuse',
                    },
                    {
                      label: trans('Lace'),
                      href: '/prom-dresses/fabric/lace',
                    },
                    {
                      label: trans('Tulle'),
                      href: '/prom-dresses/fabric/tulle',
                    },
                  ],
                },
              ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                acc.push(
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href, visible = true }, i) => {
                        if (!visible) return null;

                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>,
                );

                if (i === 1 && promDressesColorMenu) {
                  acc.push(<Fragment key='prom-dresses'>{promDressesColorMenu}</Fragment>);
                }

                return acc;
              }, [])}
            </ul>

            <ul className=' flex flex-col gap-y-[30px]'>
              {[
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/prom-new-arrivals.png`,
                  label: trans('NEW ARRIVALS'),
                  href: '/prom-dresses?sort_by=new',
                },
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/prom-floral-prom-dresses.png`,
                  label: trans('FLORAL PROM DRESSES'),
                  href: `/prom-dresses/colors-family/floral`,
                },
              ].map(({ src, label, href }, i) => {
                return (
                  <li key={i}>
                    <HoverPrefetchLink
                      href={href}
                      className='peer block'
                    >
                      <Image
                        src={src}
                        width={220}
                        height={320}
                        alt={label}
                        quality={90}
                      />
                    </HoverPrefetchLink>
                    <HoverPrefetchLink
                      href={href}
                      className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
                    >
                      {label}
                    </HoverPrefetchLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
      },
      {
        visible: isAttire,
        label: trans('HOMECOMING'),
        href: '/homecoming-dresses',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('HOMECOMING DRESSES'),
                  children: [
                    {
                      label: trans('All Homecoming Dresses'),
                      href: '/homecoming-dresses',
                    },
                    {
                      label: trans('Short'),
                      href: '/short-homecoming-dresses',
                    },
                    {
                      label: trans('Tight'),
                      href: '/tight-homecoming-dresses',
                    },
                    {
                      label: websiteEnv.isSweden ? trans('Size 48-62') : trans('Plus Size'),
                      href: '/plus-size-homecoming-dresses',
                      visible: false,
                    },
                    {
                      label: trans('Sequins'),
                      href: '/homecoming-dresses/fabric/sequin',
                    },
                    {
                      label: trans('Tiered'),
                      href: '/homecoming-dresses/embellishment/tiers',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY FABRIC'),
                  children: [
                    {
                      label: trans('Satin'),
                      href: '/homecoming-dresses/fabric/satin-charmeuse',
                      visible: false,
                    },
                    {
                      label: trans('Lace'),
                      href: '/homecoming-dresses/fabric/lace',
                      visible: false,
                    },
                    {
                      label: trans('Tulle'),
                      href: '/homecoming-dresses/fabric/tulle',
                    },
                  ],
                },
              ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                acc.push(
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href, visible = true }, i) => {
                        if (!visible) return null;

                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>,
                );

                if (i === 0 && homecomingDressesColorMenu) {
                  acc.push(<Fragment key='homecoming-dresses'>{homecomingDressesColorMenu}</Fragment>);
                }

                return acc;
              }, [])}
            </ul>

            <div>
              <HoverPrefetchLink
                href='/short-homecoming-dresses'
                className='peer block'
              >
                <Image
                  src={process.env.NEXT_PUBLIC_BASE_PATH + `/images/home/homecoming-short-homecoming-dresses.png`}
                  width={463}
                  height={320}
                  alt={trans('SHORT HOMECOMING DRESSES')}
                  className='h-auto w-full'
                  quality={90}
                />
              </HoverPrefetchLink>
              <HoverPrefetchLink
                href='/short-homecoming-dresses'
                className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
              >
                {trans('SHORT HOMECOMING DRESSES')}
              </HoverPrefetchLink>
            </div>
          </div>
        ),
      },
      {
        visible: isAttire,
        label: trans('GRADUATION'),
        href: '/graduation-dresses/colors-family/white',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('GRADUATION DRESSES'),
                  children: [
                    {
                      label: trans('All Graduation Dresses'),
                      href: '/graduation-dresses/colors-family/white',
                    },
                    {
                      label: websiteEnv.isCanadaFrench ? trans('Under CA$100') : trans('Under €100'),
                      href: '/graduation-dresses/price/0-100',
                      visible: !websiteEnv.isSweden,
                    },
                    {
                      label: trans('Short'),
                      href: '/short-graduation-dresses/colors-family/white',
                    },
                    {
                      label: trans('Long'),
                      href: '/long-graduation-dresses/colors-family/white',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY STYLE'),
                  children: [
                    {
                      label: trans('A-Line'),
                      href: '/graduation-dresses/silhouette/a-line',
                    },
                    {
                      label: trans('Mermaid'),
                      href: '/graduation-dresses/silhouette/mermaid',
                    },
                    {
                      label: trans('Sheath'),
                      href: '/graduation-dresses/silhouette/sheath',
                      visible: false,
                    },
                  ],
                },
              ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                acc.push(
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href, visible = true }, i) => {
                        if (!visible) return null;

                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>,
                );

                if (i === 1 && graduationDressesColorMenu) {
                  acc.push(<Fragment key='graduation-dresses'>{graduationDressesColorMenu}</Fragment>);
                }

                return acc;
              }, [])}
            </ul>

            <ul className=' flex flex-col gap-y-[30px]'>
              {[
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/graduation-new-arrivals.png`,
                  label: trans('NEW ARRIVALS'),
                  href: '/graduation-dresses?sort_by=new',
                },
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/graduation-long-graduation-dresses.png`,
                  label: trans('LONG GRADUATION DRESSES'),
                  href: '/long-graduation-dresses/colors-family/white',
                },
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/graduation-white-graduation-dresses.png`,
                  label: trans('WHITE GRADUATION DRESSES'),
                  href: `/graduation-dresses/colors-family/white`,
                },
              ].map(({ src, label, href }, i) => {
                return (
                  <li key={i}>
                    <HoverPrefetchLink
                      href={href}
                      className='peer block'
                    >
                      <Image
                        src={src}
                        width={220}
                        height={320}
                        alt={label}
                        quality={90}
                      />
                    </HoverPrefetchLink>
                    <HoverPrefetchLink
                      href={href}
                      className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
                    >
                      {label}
                    </HoverPrefetchLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
      },
      {
        visible: !isAttire,
        labelCellClassName: 'relative',
        label: (
          <span className='relative flex items-center gap-x-[5px]'>
            <span>{trans('BRIDESMAIDS')}</span>
            <span className='absolute -top-4 right-0 origin-top-right scale-[.8] rounded-[3px] bg-E7DED5 px-[5px] py-0.5 text-xs leading-[17px]'>
              {trans('FREE CUSTOMIZATION')}
            </span>
          </span>
        ),
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('BRIDESMAID DRESSES'),
                  children: [
                    {
                      label: trans('All Bridesmaid Dresses'),
                      href: '/bridesmaid-dresses',
                    },
                    {
                      label: trans('Long'),
                      href: '/long-bridesmaid-dresses',
                    },
                    {
                      label: trans('Short'),
                      href: '/short-bridesmaid-dresses',
                    },
                    {
                      label: trans('Velvet'),
                      href: '/bridesmaid-dresses/fabric/velvet',
                    },
                    {
                      label: trans('Floral'),
                      href: '/floral-bridesmaid-dresses',
                    },
                    {
                      label: trans('Convertible'),
                      href: '/convertible-bridesmaid-dresses',
                    },
                    {
                      label: trans('Jumpsuits'),
                      href: '/bridesmaid-dresses/silhouette/jumpsuit',
                    },
                    {
                      label: websiteEnv.isSweden ? trans('Size 48-62') : trans('Plus Size'),
                      href: '/plus-size-dresses',
                    },
                    {
                      label: trans('Maternity'),
                      href: '/maternity-bridesmaid-dresses',
                    },
                    {
                      label: trans('Junior'),
                      href: '/junior-bridesmaid-dresses',
                    },
                    {
                      label: trans('(Free!) Swatches'),
                      href: '/swatches',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY FABRIC'),
                  children: [
                    {
                      label: trans('All Fabric'),
                      href: '/shop-by-fabric',
                    },
                    {
                      label: browserStore.isMexico ? 'Satén charmeuse' : trans('Satin Charmeuse'),
                      href: '/bridesmaid-dresses/fabric/satin-charmeuse',
                    },
                    {
                      label: trans('Crystal Satin'),
                      href: '/bridesmaid-dresses/fabric/crystal-satin',
                    },
                    {
                      label: trans('Chiffon'),
                      href: '/bridesmaid-dresses/fabric/chiffon',
                    },
                    {
                      label: trans('Velvet'),
                      href: '/bridesmaid-dresses/fabric/velvet',
                    },
                    {
                      label: trans('Stretch knitted fabric'),
                      href: '/bridesmaid-dresses/fabric/stretch-knitted-fabric',
                    },
                    {
                      label: trans('Lace'),
                      href: '/bridesmaid-dresses/fabric/lace',
                    },
                  ],
                },
                {
                  label: trans('EXPLORE'),
                  children: [
                    {
                      label: trans('Free Moodboard'),
                      href: '/free-moodboard',
                    },
                    {
                      label: trans('Real Wedding'),
                      href: '/real-wedding',
                    },
                    {
                      label: trans('Size Guide'),
                      href: '/size-chart',
                    },
                    {
                      label: trans('Fall Bridesmaid Dress Color Palette'),
                      href: '/fall-bridesmaid-dresses',
                    },
                    {
                      label: trans('Jackets & Wraps'),
                      href: '/wedding-jackets-wraps',
                    },
                  ],
                },
              ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                acc.push(
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href }, i) => {
                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>,
                );

                if (i === 0 && bridesmaidColorMenu) {
                  acc.push(<Fragment key='bridesmaid-dresses'>{bridesmaidColorMenu}</Fragment>);
                }

                return acc;
              }, [])}
            </ul>

            <div>
              <HoverPrefetchLink
                href='/bridesmaid-dresses?sort_by=best_sellers'
                className='peer block'
              >
                <Image
                  src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/bridesmaids-best-seller.png`}
                  width={463}
                  height={320}
                  alt={trans('BEST SELLER')}
                  className='h-auto w-full'
                  quality={90}
                />
              </HoverPrefetchLink>
              <HoverPrefetchLink
                href='/bridesmaid-dresses?sort_by=best_sellers'
                className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
              >
                {trans('BEST SELLER')}
              </HoverPrefetchLink>
            </div>
          </div>
        ),
      },
      {
        label: trans('BRIDES'),
        visible: !isAttire,
        href: '/wedding-dresses',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('WEDDING DRESSES'),
                  children: [
                    {
                      label: trans('All Wedding Dresses'),
                      href: '/wedding-dresses',
                    },
                    {
                      label: trans('New Arrivals'),
                      href: '/wedding-dresses?sort_by=new',
                    },
                    {
                      label: websiteEnv.isSweden ? trans('Size 48-62') : trans('Plus Size'),
                      href: '/plus-size-wedding-dresses',
                    },
                    {
                      label: trans('Little White Dresses'),
                      href: '/little-white-dresses',
                    },
                    {
                      label: trans('Black Wedding Dresses'),
                      href: '/wedding-dresses/color/black',
                    },
                    {
                      label: trans('Floral Wedding Dresses'),
                      href: '/floral-wedding-dresses',
                    },
                    {
                      label: trans('Convertible Wedding Dresses'),
                      href: '/convertible-wedding-dresses',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY STYLE'),
                  children: [
                    {
                      label: trans('Boho'),
                      href: '/boho-wedding-dresses',
                    },
                    {
                      label: trans('Lace'),
                      href: '/wedding-dresses/fabric/lace',
                    },
                    {
                      label: trans('Beach'),
                      href: '/beach-wedding-dresses',
                    },
                    {
                      label: trans('Long Sleeves'),
                      href: '/wedding-dresses/straps-sleeves/long-sleeves',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY SILHOUETTE'),
                  children: [
                    {
                      label: trans('Mermaid'),
                      href: '/wedding-dresses/silhouette/mermaid',
                    },
                    {
                      label: trans('A Line'),
                      href: '/wedding-dresses/silhouette/a-line',
                    },
                    {
                      label: trans('Ball Gown'),
                      href: '/wedding-dresses/silhouette/ball-gown',
                    },
                    {
                      label: trans('Sheath'),
                      href: '/wedding-dresses/silhouette/sheath',
                    },
                  ],
                },
              ].map(({ label, children }, i) => {
                return (
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href }, i) => {
                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>

            <div className=' flex-none'>
              <HoverPrefetchLink
                href='/boho-wedding-dresses'
                className='peer block'
              >
                <Image
                  src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/brides-boho-wedding-dresses.png`}
                  width={463}
                  height={320}
                  alt={trans('BOHO WEDDING DRESSES')}
                  quality={90}
                />
              </HoverPrefetchLink>
              <HoverPrefetchLink
                href='/boho-wedding-dresses'
                className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
              >
                {trans('BOHO WEDDING DRESSES')}
              </HoverPrefetchLink>
            </div>
          </div>
        ),
      },
      {
        label: trans('GUEST'),
        visible: true,
        href: '/wedding-guest-dresses',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('WEDDING GUEST DRESSES'),
                  children: [
                    {
                      label: trans('All Wedding Guest Dresses'),
                      href: '/wedding-guest-dresses',
                    },
                    {
                      label: trans('Best Seller'),
                      href: '/wedding-guest-dresses?sort_by=best_sellers',
                    },
                    {
                      label: trans('Black Tie Wedding Guest'),
                      href: '/black-tie-wedding-guest-dresses',
                    },
                    {
                      label: trans('Spring Wedding Guest'),
                      href: '/spring-wedding-guest-dresses',
                    },
                    {
                      label: trans('Plus Size Wedding Guest'),
                      href: '/plus-size-wedding-guest-dresses',
                    },
                  ],
                },
                {
                  label: trans('DRESS CODE'),
                  children: [
                    {
                      label: trans('Casual'),
                      href: '/casual-wedding-guest-dresses',
                    },
                    {
                      label: trans('Cocktail'),
                      href: '/cocktail-wedding-guest-dresses',
                    },
                    {
                      label: trans('Beach'),
                      href: '/beach-wedding-guest-dresses',
                    },
                    {
                      label: trans('Garden'),
                      href: '/garden-wedding-guest-dresses',
                    },
                    {
                      label: trans('Black Tie'),
                      href: '/black-tie-wedding-guest-dresses',
                    },
                    {
                      label: trans('Country', { context: 'header-menu' }),
                      href: '/country-wedding-guest-dresses',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY SEASON'),
                  children: [
                    {
                      label: trans('Spring'),
                      href: '/spring-wedding-guest-dresses',
                    },
                    {
                      label: trans('Summer'),
                      href: '/summer-wedding-guest-dresses',
                    },
                    {
                      label: trans('Autumn'),
                      href: '/fall-wedding-guest-dresses',
                    },
                    {
                      label: trans('Winter'),
                      href: '/winter-wedding-guest-dresses',
                    },
                  ],
                },
              ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                acc.push(
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href }, i) => {
                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>,
                );

                if (i === 0 && guestColorMenu) {
                  acc.push(<Fragment key='wedding-guest-dresses'>{guestColorMenu}</Fragment>);
                }

                return acc;
              }, [])}
            </ul>

            <ul className=' flex flex-col gap-y-[30px]'>
              {[
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/guest-black-tie-wedding-guest.png`,
                  label: trans('BLACK TIE WEDDING GUEST'),
                  href: '/black-tie-wedding-guest-dresses',
                },

                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/guest-casual-wedding-guest.png`,
                  label: trans('CASUAL WEDDING GUEST DRESSES'),
                  href: '/casual-wedding-guest-dresses',
                },
              ].map(({ src, label, href }, i) => {
                return (
                  <li key={i}>
                    <HoverPrefetchLink
                      href={href}
                      className='peer block'
                    >
                      <Image
                        src={src}
                        width={220}
                        height={320}
                        alt={label}
                        quality={90}
                      />
                    </HoverPrefetchLink>
                    <HoverPrefetchLink
                      href={href}
                      className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
                    >
                      {label}
                    </HoverPrefetchLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
      },
      {
        label: trans('MOMS'),
        visible: !isAttire,
        href: '/mother-of-the-bride-dresses',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('MOTHER OF THE BRIDE DRESSES'),
                  children: [
                    {
                      label: trans('All Mother of the Bride Dresses'),
                      href: '/mother-of-the-bride-dresses',
                    },
                    {
                      label: trans('New Arrivals'),
                      href: '/mother-of-the-bride-dresses?sort_by=new',
                    },
                    {
                      label: websiteEnv.isCanadaFrench ? trans('Under CA$100') : trans('Under €100'),
                      href: '/mother-of-the-bride-dresses/price/0-100',
                      visible: !websiteEnv.isSweden,
                    },
                    {
                      label: trans('Spring Mother of the Bride Dresses'),
                      href: '/spring-mother-of-the-bride-dresses',
                    },
                    {
                      label: trans('Jumpsuits'),
                      href: '/mother-of-the-bride-dresses/silhouette/jumpsuit',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY STYLE'),
                  children: [
                    {
                      label: trans('With Wraps'),
                      href: '/mother-of-the-bride-dresses-with-wraps',
                    },
                    {
                      label: trans('A-Line'),
                      href: '/mother-of-the-bride-dresses/silhouette/a-line',
                    },
                    {
                      label: trans('Lace'),
                      href: '/mother-of-the-bride-dresses/fabric/lace',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY LENGTH'),
                  children: [
                    {
                      label: trans('Knee'),
                      href: '/mother-of-the-bride-dresses/hemline/knee-length',
                    },
                    {
                      label: trans('Tea'),
                      href: '/mother-of-the-bride-dresses/hemline/tea-length',
                    },
                    {
                      label: trans('Ankle'),
                      href: '/mother-of-the-bride-dresses/hemline/ankle-length',
                    },
                  ],
                },
              ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                acc.push(
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href, visible = true }, i) => {
                        if (!visible) return false;

                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' block  text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>,
                );

                if (i === 0 && momsColorMenu) {
                  acc.push(<Fragment key='mother-of-the-bride-dresses'>{momsColorMenu}</Fragment>);
                }

                return acc;
              }, [])}
            </ul>

            <ul className=' flex flex-col gap-y-[30px]'>
              {[
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/mother-of-the-bride-dresses.png`,
                  label: trans('MOTHER OF THE BRIDE DRESSES'),
                  href: '/mother-of-the-bride-dresses',
                },
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/moms-jumpsuits.png`,
                  label: trans('JUMPSUITS'),
                  href: '/mother-of-the-bride-dresses/silhouette/jumpsuit',
                },
              ].map(({ src, label, href }, i) => {
                return (
                  <li key={i}>
                    <HoverPrefetchLink
                      href={href}
                      className='peer block'
                    >
                      <Image
                        src={src}
                        width={220}
                        height={320}
                        alt={label}
                        quality={90}
                      />
                    </HoverPrefetchLink>
                    <HoverPrefetchLink
                      href={href}
                      className='mt-2.5 block max-w-max text-[15px] leading-[22px]'
                    >
                      {label}
                    </HoverPrefetchLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
      },
      {
        label: trans('DRESSES'),
        visible: !isAttire,
        href: '/dresses',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('FEATURED'),
                  children: [
                    {
                      label: trans('All Dresses'),
                      href: '/dresses',
                    },
                    {
                      label: trans('Floral Dresses'),
                      href: '/floral-dresses',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY OCCASION'),
                  children: [
                    {
                      label: trans('Prom Dresses'),
                      href: '/prom-dresses',
                    },
                    {
                      label: trans('Homecoming Dresses'),
                      href: '/homecoming-dresses',
                    },
                    {
                      label: trans('Graduation Dresses'),
                      href: '/graduation-dresses/colors-family/white',
                    },
                    {
                      label: trans('Formal Dresses'),
                      href: '/formal-dresses',
                    },
                    {
                      label: trans('Evening Dresses'),
                      href: '/evening-dresses',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY LENGTH'),
                  children: [
                    {
                      label: trans('Mini Dress'),
                      href: '/dresses/hemline/knee-length',
                    },
                    {
                      label: trans('Midi Dress'),
                      href: '/dresses/hemline/tea-length',
                    },
                    {
                      label: trans('Maxi Dress'),
                      href: '/dresses/hemline/floor-length',
                    },
                  ],
                },
                {
                  label: trans('SHOP BY FABRIC'),
                  children: [
                    {
                      label: browserStore.isMexico ? 'Satén charmeuse' : trans('Satin Charmeuse'),
                      href: '/dresses/fabric/satin-charmeuse',
                    },
                    {
                      label: trans('Velvet'),
                      href: '/dresses/fabric/velvet',
                    },
                    {
                      label: trans('Tulle'),
                      href: '/dresses/fabric/tulle',
                    },
                    {
                      label: trans('Composite Filament'),
                      href: '/dresses/fabric/composite-filament',
                    },
                  ],
                },
              ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                acc.push(
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href }, i) => {
                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>,
                );

                if (i === 2 && dressesColorMenu) {
                  acc.push(<Fragment key='dresses'>{dressesColorMenu}</Fragment>);
                }

                return acc;
              }, [])}
            </ul>

            <div>
              <HoverPrefetchLink
                href='/floral-dresses'
                className='peer block'
              >
                <Image
                  src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/dresses-best-seller.png`}
                  width={463}
                  height={320}
                  alt={trans('BEST SELLER')}
                  quality={90}
                />
              </HoverPrefetchLink>
              <HoverPrefetchLink
                href='/floral-dresses'
                className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
              >
                {trans('BEST SELLER')}
              </HoverPrefetchLink>
            </div>
          </div>
        ),
      },
      {
        label: trans('FABRIC & SWATCHES'),
        visible: !isAttire,
        href: '/swatches',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('FABRIC & SWATCHES'),
                  children: [
                    {
                      label: trans('(Free!) Swatches'),
                      href: '/swatches',
                    },
                    {
                      label: trans('Fabric By Yard'),
                      href: '/fabrics',
                    },
                  ],
                },
              ].map(({ label, children }, i) => {
                return (
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href }, i) => {
                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>

            <ul className=' flex flex-col gap-y-[30px]'>
              {[
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/fabric-swatches-velvet.png`,
                  label: trans('SATIN CHARMEUSE'),
                  href: '/swatches/fabric/satin-charmeuse',
                },
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/fabric-swatches-satin-charmeuse.png`,
                  label: trans('VELVET'),
                  href: '/swatches/fabric/velvet',
                },
              ].map(({ src, label, href }, i) => {
                return (
                  <li key={i}>
                    <HoverPrefetchLink
                      href={href}
                      className='peer block'
                    >
                      <Image
                        src={src}
                        width={463}
                        height={320}
                        alt={label}
                        quality={90}
                      />
                    </HoverPrefetchLink>
                    <HoverPrefetchLink
                      href={href}
                      className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
                    >
                      {label}
                    </HoverPrefetchLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
      },
      {
        label: trans('SALE'),
        visible: !isAttire,
        href: '/wedding-sales',
        dropdown: (
          <div className='flex flex-col gap-y-[30px] bg-white p-5'>
            <ul className='flex flex-col gap-y-[30px]'>
              {[
                {
                  label: trans('SALE'),
                  children: [
                    {
                      label: trans('All Sale'),
                      href: '/wedding-sales',
                    },
                    {
                      label: trans('Sale Bridesmaid Dresses'),
                      href: '/bridesmaid-dresses-sale',
                    },
                    {
                      label: trans('Sale Wedding Dresses'),
                      href: '/wedding-dresses-sale',
                    },
                  ],
                },
              ].map(({ label, children }, i) => {
                return (
                  <li key={i}>
                    <span className='text-[15px] font-medium leading-[22px]'>{label}</span>

                    <ul className='mt-[15px] flex flex-col gap-y-1.5'>
                      {children.map(({ label, href }, i) => {
                        return (
                          <li key={i}>
                            <HoverPrefetchLink
                              href={href}
                              className=' text-[15px] leading-[22px]'
                            >
                              {label}
                            </HoverPrefetchLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>

            <ul className=' flex flex-col gap-y-[30px]'>
              {[
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/sale-sale-bridesmaid-dresses.png`,
                  label: trans('SALE BRIDESMAID DRESSES'),
                  href: '/bridesmaid-dresses-sale',
                },
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/sale-sale-dresses.png`,
                  label: trans('SALE DRESSES'),
                  href: '/wedding-sales',
                },
                {
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/sale-sale-wedding-dress.png`,
                  label: trans('SALE WEDDING DRESSES'),
                  href: '/wedding-dresses-sale',
                },
              ].map(({ src, label, href }, i) => {
                return (
                  <li key={i}>
                    <HoverPrefetchLink
                      href={href}
                      className=' block'
                    >
                      <Image
                        src={src}
                        width={320}
                        height={320}
                        alt={label}
                        quality={90}
                      />
                    </HoverPrefetchLink>
                    <HoverPrefetchLink
                      href={href}
                      className=' mt-2.5 block max-w-max text-[15px] leading-[22px]'
                    >
                      {label}
                    </HoverPrefetchLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
      },
    ];
  }, [colorMenus, isAttire]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const eventTarget = e.target;

      if (
        eventTarget &&
        eventTarget instanceof Element &&
        !drawerContentRef.current?.contains(eventTarget) &&
        !getNavTriggerElement()?.contains(eventTarget)
      ) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handler);
    }

    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open, onOpenChange, getNavTriggerElement]);

  return (
    <>
      {createPortal(
        <style>
          {`
            .${MOBILE_MENU_DRAWER_CONTENT_CLASS_NAME}{
              height:calc(100vh - ${headerVisibleHeight}px);
            }

            @supports (height:100dvh) {
              .${MOBILE_MENU_DRAWER_CONTENT_CLASS_NAME}{
                height:calc(100dvh - ${headerVisibleHeight}px);
              }
            }

            ${open ? '#heroDash-chat-widget-iframe{display:none !important;}' : null}
          `}
        </style>,
        document.head,
      )}

      <RemoveScroll enabled={open}>
        <div
          style={{ top: headerVisibleHeight }}
          className={clsx(
            MOBILE_MENU_DRAWER_CONTENT_CLASS_NAME,
            open ? 'block bg-black/50' : 'hidden bg-transparent',
            'pointer-events-none fixed left-0 z-[300] w-full',
            'transition-[background-color] [transition-duration:600ms]',
          )}
        />
        <div
          className={clsx(
            ' fixed left-0 z-[400] w-full border-none outline-0 focus_border-none focus_outline-0 ',
            'transition-opacity duration-2xl',
            MOBILE_MENU_DRAWER_CONTENT_CLASS_NAME,
            open ? 'block animate-in slide-in-from-left-[100%]' : 'hidden animate-out slide-out-to-left-[100%]',
          )}
          style={{ top: headerVisibleHeight }}
          ref={drawerContentRef}
        >
          <div className={clsx('flex h-full w-full overflow-hidden ')}>
            <div
              className='flex h-full w-full  flex-col bg-f8f3ef outline-0 focus_outline-0 min-[300px]_basis-[80%]'
              onClick={handleClick}
            >
              <NavigationAvatar />
              <div className='flex-1 overflow-hidden '>
                <div className=' h-full overflow-auto text-#403732'>
                  <HoverPrefetchLink
                    href='/swatches'
                    className={clsx(
                      'flex items-center gap-x-2.5 px-[15px] py-[18px] text-base',
                      'border-b-1 border-solid border-E7DED5',
                    )}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='22'
                      height='26'
                      fill='none'
                      viewBox='0 0 22 26'
                    >
                      <g
                        fill='#80583F'
                        clipPath='url(#clip0_13022_66323)'
                      >
                        <path d='M19.297 26.002H2.22A2.22 2.22 0 0 1 0 23.785V6.898h21.517v16.887a2.22 2.22 0 0 1-2.22 2.217M1.25 8.142v15.64c0 .536.437.968.97.968h17.073a.97.97 0 0 0 .97-.969V8.142z' />
                        <path d='M21.517 8.14H0V2.217A2.22 2.22 0 0 1 2.22 0h17.073a2.22 2.22 0 0 1 2.22 2.217V8.14zM1.25 6.892h19.018V2.217a.97.97 0 0 0-.97-.97H2.22a.973.973 0 0 0-.97.97z' />
                        <path d='M17.861 6.898H3.633v1.248H17.86z' />
                        <path d='M3.694 22.775h-.02c-.213-.004-.526-.083-.813-.432-.612-.74-.142-1.514.166-2.022.196-.32.4-.653.4-.957 0-.32-.104-.453-.291-.69-.238-.3-.563-.707-.563-1.464s.325-1.165.563-1.464c.187-.237.291-.366.291-.69 0-.325-.104-.454-.291-.691-.238-.3-.563-.707-.563-1.464s.325-1.165.563-1.464c.187-.237.291-.366.291-.686s-.104-.454-.291-.691c-.238-.3-.563-.707-.563-1.464s.325-1.165.563-1.464l.187-.237.304-.004H18.16l.187.237c.237.3.562.707.562 1.464s-.325 1.164-.562 1.464c-.187.237-.292.366-.292.69 0 .32.105.454.292.69.237.3.562.708.562 1.465s-.325 1.165-.562 1.464c-.187.237-.292.366-.292.69 0 .32.105.454.292.69.237.3.562.708.562 1.465 0 .72-.341 1.173-.591 1.502-.242.32-.317.432-.259.686.1.457.3.77.492 1.073.28.44.708 1.11.075 1.876-.287.35-.6.428-.812.432h-.021c-.512 0-.862-.39-1.17-.736-.138-.154-.43-.478-.546-.478s-.175.05-.396.332c-.25.33-.633.82-1.383.82s-1.133-.495-1.382-.82c-.221-.282-.28-.332-.396-.332-.117 0-.175.05-.396.332-.25.33-.633.82-1.383.82s-1.133-.495-1.382-.82c-.217-.282-.28-.332-.392-.332-.117 0-.175.05-.396.332-.25.33-.633.82-1.383.82-.753 0-1.132-.495-1.387-.82-.22-.282-.279-.332-.395-.332-.117 0-.404.324-.546.478-.304.341-.654.732-1.166.732m8.83-2.462c.754 0 1.133.495 1.383.82.216.282.279.332.396.332s.174-.05.395-.333c.25-.328.633-.82 1.383-.82.646 0 1.1.475 1.433.845-.004-.008-.013-.017-.017-.025-.216-.337-.508-.803-.658-1.476-.179-.824.204-1.331.483-1.701.221-.296.338-.462.338-.75 0-.32-.104-.453-.292-.69-.237-.3-.562-.707-.562-1.464s.325-1.164.562-1.464c.188-.237.292-.366.292-.69 0-.32-.104-.454-.292-.69-.237-.3-.562-.708-.562-1.465s.325-1.164.562-1.464c.188-.237.292-.366.292-.69 0-.2-.042-.329-.117-.45h-13.6a.8.8 0 0 0-.12.458c0 .32.104.453.292.69.237.3.562.707.562 1.465 0 .757-.325 1.164-.562 1.464-.188.237-.292.366-.292.69 0 .32.104.453.292.69.237.3.562.708.562 1.465s-.325 1.164-.562 1.464c-.188.237-.292.366-.292.69 0 .325.104.454.292.69.237.3.562.708.562 1.465 0 .653-.325 1.18-.583 1.605-.03.05-.067.104-.1.166.333-.366.779-.819 1.412-.819.754 0 1.133.495 1.387.82.22.282.279.332.395.332.117 0 .175-.05.396-.332.25-.33.633-.82 1.383-.82s1.133.495 1.383.82c.216.282.279.332.391.332.117 0 .175-.05.396-.332.254-.333.633-.828 1.387-.828M18.325 2.625H10.02v1.248h8.305zM18.329 4.852h-4.153v1.247h4.152z' />
                      </g>
                      <defs>
                        <clipPath id='clip0_13022_66323'>
                          <path
                            fill='#fff'
                            d='M0 0h21.517v26H0z'
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className='text-[17px] leading-6 text-#403732'>{trans('GET 5 FREE SWATCHES')}</span>
                  </HoverPrefetchLink>
                  {menus.map(({ label, dropdown, visible }, i) => {
                    if (!visible) return null;

                    return (
                      <NavigationItem
                        key={i}
                        label={label}
                        dropdown={dropdown}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            <div
              className={clsx('basis-0   min-[300px]_basis-[20%]')}
              onClick={() => {
                onOpenChange(false);
              }}
            ></div>
          </div>
        </div>
      </RemoveScroll>
    </>
  );
};

const generateColorMenuNode = (menus: ColorOption[], title: ReactNode, categoryUrlKey: string) => {
  return (
    <li className='flex-none'>
      <span className='text-[15px] font-medium leading-[21px]'>{title}</span>

      <ul className='mt-[15px] flex flex-col gap-y-2'>
        {menus.map((x, i) => {
          if (!x || !x.label || !x.swatch_data || !x.default_label) return null;

          const {
            label,
            default_label,
            swatch_data: { thumbnail, value },
          } = x;

          if (!thumbnail || !value) return null;

          const colorsFamilyRewrite = default_label.toLowerCase().replace(/\s/g, '-') || '';
          const href = colorsFamilyRewrite ? `${categoryUrlKey}/colors-family/${colorsFamilyRewrite}` : categoryUrlKey;

          if (colorsFamilyRewrite === 'all') {
            return null;
          }

          return (
            <li key={i}>
              <HoverPrefetchLink
                href={href}
                className='flex items-center gap-x-[7px] text-[15px] leading-[21px]'
              >
                <span
                  className={clsx(
                    'block h-5 w-5 rounded-[5px] bg-contain',
                    isWhite(value) ? 'border-1 border-solid border-E7DED5' : '',
                  )}
                  style={createColorStyle(thumbnail, value)}
                />
                <span>{label}</span>
              </HoverPrefetchLink>
            </li>
          );
        })}
      </ul>
    </li>
  );
};

const isWhite = (value: string) => {
  const lowercaseValue = value.toLowerCase();

  return lowercaseValue === '#fff' || lowercaseValue === '#ffffff';
};

const createColorStyle = (thumbnail: string | undefined, value: string | undefined) => {
  if (!thumbnail && !value) {
    return {};
  }

  if (thumbnail && thumbnail.indexOf('#') < 0) {
    const imagePath = generateUrl(thumbnail, 'image-swatch')(48);

    return {
      backgroundImage: `url("${imagePath}")`,
    };
  }

  return {
    backgroundColor: value,
  };
};

export default Navigation;
